import React, { useState } from 'react';
import { useAsync } from 'react-use';
import { useApiClient } from '../../../base/hooks/useApiClient';
import { Clock, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SessionRun {
    id: number;
    startedAt: string;
    endedAt: string | null;
    duration: number | null;
    messagesCount: number;
}

interface SessionRunsResponse {
    runs: SessionRun[];
    pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
    };
}

interface Props {
    sessionUuid: string;
}

export function SessionRunsList({ sessionUuid }: Props) {
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const limit = 5;

    const { value: runsData, loading } = useAsync(async () => {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });

        const response = await apiClient.get(`sessions/${sessionUuid}/runs?${params}`);
        if (!response.ok) {
            console.error('Error fetching session runs');
            return null;
        }

        return await response.json() as SessionRunsResponse;
    }, [sessionUuid, page]);

    const formatDuration = (milliseconds: number | null) => {
        if (milliseconds === null) return 'In Progress';
        const seconds = milliseconds / 1000;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    };

    const formatDate = (dateString: string) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(dateString));
    };

    if (loading) {
        return (
            <div className="py-8 text-center text-gray-500">
                Loading session runs...
            </div>
        );
    }

    if (!runsData || runsData.runs.length === 0) {
        return (
            <div className="py-8 text-center text-gray-500">
                No session runs found
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {runsData.runs.map((run) => (
                <div
                    key={run.id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-100 transition-colors"
                >
                    <div className="flex-1">
                        <div className="flex items-center gap-3">
                            <div className="text-sm font-medium text-gray-900">
                                {formatDate(run.startedAt)}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                                <Clock className="h-4 w-4 mr-1" />
                                {formatDuration(run.duration)}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/panel/sessions/${sessionUuid}/runs/${run.id}`);
                        }}
                        className="flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors text-sm"
                    >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                    </button>
                </div>
            ))}

            {runsData.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                        Page {runsData.pagination.page} of {runsData.pagination.totalPages}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setPage(page - 1);
                            }}
                            disabled={page === 1}
                            className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setPage(page + 1);
                            }}
                            disabled={page === runsData.pagination.totalPages}
                            className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
